import { useState, useEffect, useMemo } from "react";
import { useUpdateCampaignMutation, useDeleteCampaignMutation } from "./campaignsApiSlice";
import { useParams, useNavigate } from "react-router-dom";

// small helper: ensure value fits <input type="date">
const toDateInput = (d) => {
    if (!d) return "";
    const dt = typeof d === "string" ? new Date(d) : d;
    if (Number.isNaN(dt.getTime())) return "";
    return dt.toISOString().slice(0, 10);
};

export default function EditCampaignForm({ campaign }) {
    const { id: routeId } = useParams(); // /dash/campaigns/:id
    const navigate = useNavigate();
    const targetId = campaign?._id || routeId;

    const [updateCampaign, { isLoading, isSuccess, isError, error }] = useUpdateCampaignMutation();
    const [deleteCampaign, { isSuccess: isDelSuccess, isError: isDelError, error: delerror }] =
        useDeleteCampaignMutation();

    // --- form state (prefill from campaign) ---
    const [title, setTitle] = useState(campaign.title || "");
    const [shortTitle, setShortTitle] = useState(campaign?.shortTitle || "");
    const [description, setDescription] = useState(campaign?.description || "");
    const [startDate, setStartDate] = useState(toDateInput(campaign?.startDate));
    const [endDate, setEndDate] = useState(toDateInput(campaign?.endDate));
    const [status, setStatus] = useState(campaign?.status || "open");

    // Validation flags
    const validTitle = title.trim().length >= 3 && title.trim().length <= 80;
    const validShort = shortTitle.trim().length >= 3 && shortTitle.trim().length <= 60;
    const validDesc = description.trim().length > 0;
    const validDates = useMemo(() => {
        if (!startDate || !endDate) return false;
        const s = new Date(startDate);
        const e = new Date(endDate);
        return !Number.isNaN(s) && !Number.isNaN(e) && s <= e;
    }, [startDate, endDate]);

    const canSave = validTitle && validShort && validDesc && validDates && !!targetId && !isLoading;


    // After save/delete → go back
    useEffect(() => {
        if (isSuccess || isDelSuccess) {
        navigate("/dash/campaigns");
        }
    }, [isSuccess, isDelSuccess, navigate]);

    const onSave = async (e) => {
        e.preventDefault();
        if (!canSave) return;
        try {
        await updateCampaign({
            id: targetId,
            title: title.trim(),
            shortTitle: shortTitle.trim(),
            description: description.trim(),
            startDate, // yyyy-MM-dd (backend turns into Date)
            endDate,   // yyyy-MM-dd
            status,
        }).unwrap();
        } catch {
        // handled by error banner
        }
    };

    const onDelete = async () => {
        if (!targetId) return;
        if (!window.confirm(`Delete campaign "${title}"?`)) return;
        try {
        await deleteCampaign({id: targetId}).unwrap(); // slice uses DELETE /campaigns/:id
        } catch {
        // handled by error banner
        }
    };

    const errText = (error?.data?.message || delerror?.data?.message) ?? "";

    return (
        <div className="min-h-full px-4 sm:px-6 lg:px-8 py-6">
        {/* Page header lives in the wrapper (EditCampaign.js) for consistency */}

        {(isError || isDelError) && (
            <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-rose-700">
            {errText || "Something went wrong."}
            </div>
        )}

        <form onSubmit={(e) => e.preventDefault()} className="mt-2">
            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Title */}
                <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                    Title <span className="text-gray-400 font-normal">[3–80 chars]</span>
                </label>
                <input
                    id="title"
                    type="text"
                    autoComplete="off"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className={`mt-1 w-full rounded-xl border ${
                    validTitle ? "border-gray-200" : "border-amber-300"
                    } bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-black/5`}
                    placeholder="e.g. 2025 SDG Financing Campaign"
                />
                {!validTitle && (
                    <p className="mt-1 text-xs text-amber-600">3–80 characters.</p>
                )}
                </div>

                {/* Short Title */}
                <div>
                <label htmlFor="shorttitle" className="block text-sm font-medium text-gray-700">
                    Short Title <span className="text-gray-400 font-normal">[3–60 chars]</span>
                </label>
                <input
                    id="shorttitle"
                    type="text"
                    autoComplete="off"
                    value={shortTitle}
                    onChange={(e) => setShortTitle(e.target.value)}
                    className={`mt-1 w-full rounded-xl border ${
                    validShort ? "border-gray-200" : "border-amber-300"
                    } bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-black/5`}
                    placeholder="e.g. SDG 2025"
                />
                {!validShort && (
                    <p className="mt-1 text-xs text-amber-600">3–60 characters.</p>
                )}
                </div>

                {/* Description */}
                <div className="md:col-span-2">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                    Description
                </label>
                <textarea
                    id="description"
                    rows={4}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className={`mt-1 w-full rounded-xl border ${
                    validDesc ? "border-gray-200" : "border-amber-300"
                    } bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-black/5`}
                    placeholder="What is this campaign about?"
                />
                {!validDesc && (
                    <p className="mt-1 text-xs text-amber-600">Description is required.</p>
                )}
                </div>

                {/* Dates */}
                <div>
                <label htmlFor="startdate" className="block text-sm font-medium text-gray-700">
                    Start Date
                </label>
                <input
                    id="startdate"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className={`mt-1 w-full rounded-xl border ${
                    validDates ? "border-gray-200" : "border-amber-300"
                    } bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-black/5`}
                />
                </div>

                <div>
                <label htmlFor="enddate" className="block text-sm font-medium text-gray-700">
                    End Date
                </label>
                <input
                    id="enddate"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className={`mt-1 w-full rounded-xl border ${
                    validDates ? "border-gray-200" : "border-amber-300"
                    } bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-black/5`}
                />
                {!validDates && (
                    <p className="mt-1 text-xs text-amber-600">End date must be ≥ start date.</p>
                )}
                </div>

                {/* Status */}
                <div>
                <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                    Status
                </label>
                <select
                    id="status"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-black/5"
                >
                    <option value="open">Open</option>
                    <option value="closed">Closed</option>
                </select>
                </div>
            </div>

            {/* Footer actions */}
            <div className="mt-6 flex items-center justify-end gap-2">
                <button
                type="button"
                onClick={onDelete}
                className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                Delete
                </button>
                <button
                type="submit"
                onClick={onSave}
                disabled={!canSave}
                className={`rounded-xl px-4 py-2 text-sm text-white ${
                    canSave ? "bg-gray-900 hover:bg-black" : "bg-gray-400 cursor-not-allowed"
                }`}
                >
                Save
                </button>
            </div>
            </div>
        </form>
        </div>
    );
}
